import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { CryodOverviewParams } from "@/lib/types";

export function useCiRuns(params?: Record<string, string>) {
  return useQuery({
    queryKey: ["ci-runs", params],
    queryFn: () => api.listCiRuns(params),
    // Keep the previous page visible while the next one is fetching so the
    // table doesn't blank out between pagination clicks.
    placeholderData: keepPreviousData,
  });
}

export function useCiRunTriggerUsers() {
  return useQuery({
    queryKey: ["ci-run-trigger-users"],
    queryFn: () => api.listCiRunTriggerUsers(),
    staleTime: 60_000,
  });
}

export function useCiRun(runId: string | undefined) {
  return useQuery({
    queryKey: ["ci-run", runId],
    queryFn: () => api.getCiRun(runId as string),
    enabled: !!runId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && data.status === "in_progress") return 10_000;
      return false;
    },
  });
}

export function useCiTestResult(runId: string | undefined, resultId: number | undefined) {
  return useQuery({
    queryKey: ["ci-result", runId, resultId],
    queryFn: () => api.getCiTestResult(runId as string, resultId as number),
    enabled: !!runId && resultId != null,
  });
}

export function useCiTestNames() {
  return useQuery({
    queryKey: ["ci-test-names"],
    queryFn: () => api.listCiTestNames(),
  });
}

export function useCiOverview(params: Record<string, string>) {
  return useQuery({
    queryKey: ["ci-overview", params],
    queryFn: () => api.getCiOverview(params),
  });
}

export function useCiTrends(params: Record<string, string>) {
  return useQuery({
    queryKey: ["ci-trends", params],
    queryFn: () => api.getCiTrends(params),
    enabled: !!params.test_name,
  });
}

export function useCryodOverview(params: CryodOverviewParams) {
  return useQuery({
    queryKey: ["cryod-overview", params],
    queryFn: () => api.getCryodOverview(params),
  });
}

export function useCryodFacets() {
  return useQuery({
    queryKey: ["cryod-facets"],
    queryFn: () => api.getCryodFacets(),
    // Facets are several DISTINCT queries over a 90d window and rarely change;
    // cache so navigating overview <-> detail doesn't refetch each time.
    staleTime: 5 * 60_000,
  });
}

export function useCryodSummary(params: { window: string; filters: string[] }) {
  return useQuery({
    queryKey: ["cryod-summary", params],
    queryFn: () => api.getCryodSummary(params),
  });
}

export function useCryodRuns(params: {
  window: string;
  metric: string;
  filters: string[];
  limit?: number;
}) {
  return useQuery({
    queryKey: ["cryod-runs", params],
    queryFn: () => api.getCryodRuns(params),
  });
}

export function useCryodCatalog() {
  return useQuery({
    queryKey: ["cryod-catalog"],
    queryFn: () => api.getCryodCatalog(),
    // Catalog is parsed from static YAMLs and changes only on Fleet redeploy.
    staleTime: 5 * 60_000,
  });
}

export function useCryodTofa(tag: string | null, revision: string | null) {
  return useQuery({
    queryKey: ["cryod-tofa", tag, revision],
    placeholderData: keepPreviousData,
    queryFn: () => api.getCryodTofa(definedParams({ version: tag, revision })),
  });
}

function definedParams(entries: Record<string, string | null>): Record<string, string> {
  return Object.fromEntries(Object.entries(entries).filter(([, v]) => v) as [string, string][]);
}

export function useCryodVersions(revision: string | null = null) {
  return useQuery({
    queryKey: ["cryod-versions", revision],
    placeholderData: keepPreviousData,
    queryFn: () => api.getCryodVersions(definedParams({ revision })),
    staleTime: 60_000,
  });
}

export function useCryodEngineVersions(tag: string | null, engine: string | null, enabled = true) {
  return useQuery({
    queryKey: ["cryod-engine-versions", tag, engine],
    queryFn: () => api.getCryodEngineVersions(tag ?? undefined, engine ?? undefined),
    enabled,
    staleTime: 60_000,
  });
}

export function useCryodBuildRevisions(tag: string | null) {
  return useQuery({
    queryKey: ["cryod-build-revisions", tag],
    placeholderData: keepPreviousData,
    queryFn: () => api.getCryodBuildRevisions(definedParams({ version: tag })),
    staleTime: 60_000,
  });
}

export function useCryodMatrixStatus(
  version: string | null,
  engineVersion: string | null = null,
  revision: string | null = null,
) {
  return useQuery({
    queryKey: ["cryod-matrix-status", version, engineVersion, revision],
    queryFn: () => {
      const params: Record<string, string> = {};
      if (version) params.version = version;
      if (engineVersion) params.engine_version = engineVersion;
      if (revision) params.revision = revision;
      return api.getCryodMatrixStatus(params);
    },
    // The backend requires at least one filter; with neither there is no
    // overlay to show.
    enabled: !!version || !!engineVersion || !!revision,
    // Matches useCryodVersions and the cadence of CI results landing, so
    // toggling between tags doesn't refetch every time.
    staleTime: 60_000,
  });
}

export function useCiTestHistory(testName: string | undefined, params?: Record<string, string>) {
  return useQuery({
    queryKey: ["ci-test-history", testName, params],
    queryFn: () => api.getCiTestHistory(testName as string, params),
    enabled: !!testName,
  });
}

export function useCiTestLogs(
  runId: string | undefined,
  testName: string | undefined,
  version: string | undefined,
) {
  return useQuery({
    queryKey: ["ci-test-logs", runId, testName, version],
    queryFn: () => api.listCiTestLogs(runId as string, testName as string, version as string),
    enabled: !!runId && !!testName && !!version,
  });
}

export function useCiRunNodeLogs(runId: string | undefined, nodeName: string | undefined) {
  return useQuery({
    queryKey: ["ci-run-node-logs", runId, nodeName],
    queryFn: () => api.listCiRunNodeLogs(runId as string, nodeName as string),
    enabled: !!runId && !!nodeName,
  });
}

export function useCiJobOverviews(params?: Record<string, string>) {
  return useQuery({
    queryKey: ["ci-jobs-overview", params ?? {}],
    queryFn: () => api.listCiJobOverviews(params),
    refetchInterval: 15_000,
  });
}

export function useCiJob(jobId: string | undefined) {
  return useQuery({
    queryKey: ["ci-job", jobId],
    queryFn: () => api.getCiJob(jobId as string),
    enabled: Boolean(jobId),
    // Live board: a running job updates progress via heartbeats.
    refetchInterval: 10_000,
  });
}
